/**
 * @module AdapterCard
 * @package @geenius/adapters/react-ant
 * @description Renders the Ant Design tokenized adapter summary card for the React
 * variant. The card surfaces provider status, latency, and domain metadata for
 * one adapter domain in the dashboard views.
 */

import type { AdapterDomain, AdapterStatusInfo } from "@geenius/adapters";
import {
  DOMAIN_DESCRIPTIONS,
  DOMAIN_ICONS,
  DOMAIN_LABELS,
} from "@geenius/adapters";
import theme from "antd/lib/theme/index.js";
import type { JSX } from "react";
import { AdapterStatusBadge } from "./AdapterStatusBadge";

/**
 * Props for rendering a single adapter summary card in the React dashboard grid.
 *
 * @property domain Adapter domain represented by the card.
 * @property status Current provider health and metadata for the domain.
 * @property onClick Optional callback fired when the card is activated.
 * @property className Optional class name merged into the card container.
 * @property style Optional inline styles merged into the card container.
 */
export interface AdapterCardProps {
  domain: AdapterDomain;
  status: AdapterStatusInfo;
  onClick?: () => void;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Renders a clickable React summary card for one adapter domain.
 *
 * @param props Card props including the adapter domain, status payload, and click handler.
 * @returns A button element that summarizes provider state for the selected domain.
 */
export function AdapterCard({
  domain,
  status,
  onClick,
  className,
  style,
}: AdapterCardProps): JSX.Element {
  const { token } = theme.useToken();

  return (
    <button
      type="button"
      onClick={onClick}
      className={className}
      style={{
        position: "relative",
        display: "flex",
        width: "100%",
        alignItems: "flex-start",
        gap: token.margin,
        overflow: "hidden",
        borderRadius: token.borderRadiusLG,
        border: `1px solid ${token.colorBorder}`,
        backgroundColor: token.colorBgContainer,
        padding: token.paddingLG,
        textAlign: "left",
        color: token.colorText,
        boxShadow: token.boxShadowTertiary,
        transition: "all 0.2s",
        cursor: "pointer",
        ...style,
      }}
      aria-label={`${DOMAIN_LABELS[domain]} adapter - ${status.status}`}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = token.colorPrimaryBorder;
        e.currentTarget.style.backgroundColor = token.colorBgLayout;
        e.currentTarget.style.boxShadow = token.boxShadowSecondary;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = token.colorBorder;
        e.currentTarget.style.backgroundColor = token.colorBgContainer;
        e.currentTarget.style.boxShadow = token.boxShadowTertiary;
      }}
    >
      <div
        style={{
          display: "flex",
          height: 48,
          width: 48,
          flexShrink: 0,
          alignItems: "center",
          justifyContent: "center",
          borderRadius: token.borderRadius,
          border: `1px solid ${token.colorBorder}`,
          backgroundColor: token.colorBgLayout,
          fontSize: 24,
          boxShadow: token.boxShadowTertiary,
        }}
      >
        {DOMAIN_ICONS[domain]}
      </div>

      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: token.marginXS,
          }}
        >
          <span
            style={{
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: token.fontSizeSM,
              fontWeight: "bold",
              color: token.colorTextHeading,
              margin: 0,
            }}
          >
            {DOMAIN_LABELS[domain]}
          </span>
          <AdapterStatusBadge status={status.status} />
        </div>

        <p
          style={{
            marginTop: 2,
            fontSize: 12,
            color: token.colorTextSecondary,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            margin: 0,
          }}
        >
          {DOMAIN_DESCRIPTIONS[domain]}
        </p>

        {status.provider && status.provider !== "none" && (
          <div
            style={{
              marginTop: token.marginXS,
              display: "flex",
              alignItems: "center",
              gap: token.marginXS,
            }}
          >
            <span
              style={{
                borderRadius: token.borderRadiusSM,
                border: `1px solid ${token.colorBorder}`,
                backgroundColor: token.colorBgLayout,
                padding: "2px 8px",
                fontSize: 10,
                fontWeight: 600,
                color: token.colorTextSecondary,
              }}
            >
              {status.provider}
            </span>
            {status.latency && (
              <span style={{ fontSize: 10, color: token.colorTextSecondary }}>
                {status.latency}ms
              </span>
            )}
          </div>
        )}

        {status.error && (
          <p
            style={{
              marginTop: 6,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: 10,
              color: token.colorText,
              margin: "6px 0 0 0",
            }}
          >
            {status.error}
          </p>
        )}
      </div>
    </button>
  );
}
